import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCities } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import LocationPicker from "@/components/LocationPicker";
import PhotoPicker from "@/components/PhotoPicker";
import ProviderServicesPick from "@/components/ProviderServicesPick";
import BackButton from "@/components/BackButton";
import { NotificationSetup } from "@/components/InstallHelp";

function AreasPick({ areas, onChange }) {
  const { data: cities = [] } = useCities();
  const get = (name) => areas.find((a) => a.city === name);
  const toggleCity = (name) => onChange(get(name) ? areas.filter((a) => a.city !== name) : [...areas, { city: name, neighborhoods: [] }]);
  const toggleNb = (city, nb) => onChange(areas.map((a) => a.city !== city ? a : { ...a, neighborhoods: a.neighborhoods.includes(nb) ? a.neighborhoods.filter((n) => n !== nb) : [...a.neighborhoods, nb] }));
  return (
    <div>
      <p className="po-label">Cidades e bairros onde você atende</p>
      <p className="mb-3 text-base text-slate-600">Se não escolher bairros, você atende a cidade toda.</p>
      <div className="space-y-3">
        {cities.map((c) => {
          const a = get(c.name);
          return (
            <div key={c.id} className={`rounded-2xl border-2 p-3 ${a ? "border-orange-400 bg-orange-50/50" : "border-slate-200"}`}>
              <label className="flex min-h-[44px] items-center gap-3 text-lg font-bold text-slate-800">
                <input type="checkbox" className="h-6 w-6 accent-orange-500" checked={!!a} onChange={() => toggleCity(c.name)} data-testid={`pf-city-${c.name}`} /> {c.name}
                {a && <span className="text-base font-medium text-slate-600">{a.neighborhoods.length ? `${a.neighborhoods.length} bairro(s)` : "cidade toda"}</span>}
              </label>
              {a && c.neighborhoods.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.neighborhoods.map((n) => (
                    <button type="button" key={n.name} onClick={() => toggleNb(c.name, n.name)}
                      className={`rounded-full border-2 px-3 py-1.5 text-base font-semibold ${a.neighborhoods.includes(n.name) ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 bg-white text-slate-700"}`}>
                      {n.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProviderForm() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const p = user.provider;
  const [f, setF] = useState({
    name: p?.name || user.name || "", photo: p?.photo || null, whatsapp: p?.whatsapp || user.whatsapp || "",
    services: p?.services || [], primary_service: p?.primary_service || "", areas: p?.areas || [],
    base_city: p?.base_city || "", base_neighborhood: p?.base_neighborhood || "", description: p?.description || "",
  });
  const [agree, setAgree] = useState(!!p);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.areas.length) return toast.error("Escolha pelo menos uma cidade onde você atende.");
    if (!f.base_city || !f.base_neighborhood) return toast.error("Informe o bairro onde você fica.");
    if (!agree) return toast.error("Aceite os termos de uso.");
    setBusy(true);
    try {
      await api.put("/provider/profile", f);
      await refresh();
      toast.success(p ? "Cadastro atualizado!" : "Cadastro enviado para análise!");
      if (p) navigate("/perfil");
      else {
        // Meta Pixel: only on a successful new provider signup; no personal data sent.
        if (typeof window.fbq === "function") window.fbq("track", "CompleteRegistration");
        setSaved(true);
        window.scrollTo(0, 0);
      }
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (saved) {
    return (
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6" data-testid="provider-saved">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Cadastro enviado!</h1>
        <p className="rounded-3xl bg-yellow-50 p-5 text-lg text-yellow-900" data-testid="provider-welcome-message">{f.name.trim() ? `Olá, ${f.name.trim().split(" ")[0]}!` : "Olá!"} Seu cadastro está em análise. Em poucos minutos você estará apto a trabalhar.</p>
        <p className="text-lg text-slate-700">Enquanto isso, ative os avisos de pedidos:</p>
        <NotificationSetup />
        <Link to="/perfil" className="po-btn-primary w-full" data-testid="provider-saved-continue">Ir para meu perfil</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
      <BackButton to={p ? "/perfil" : "/"} />
      <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">{p ? "Editar cadastro de prestador" : "Quero ser prestador"}</h1>
      <p className="mt-2 text-lg text-slate-700">Preencha seus dados. Todo cadastro novo passa por análise antes de aparecer para os clientes.</p>
      <form onSubmit={submit} className="mt-6 space-y-6" data-testid="provider-form">
        <div><p className="po-label">Sua foto</p><PhotoPicker value={f.photo ? [f.photo] : []} onChange={(v) => setF({ ...f, photo: v[0] || null })} max={1} kind="provider" label="Enviar foto" /></div>
        <div><label className="po-label" htmlFor="pf-name">Nome que aparece para os clientes</label>
          <input id="pf-name" className="po-input" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pf-name-input" /></div>
        <div><label className="po-label" htmlFor="pf-wa">WhatsApp</label>
          <input id="pf-wa" type="tel" className="po-input" required minLength={10} value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} placeholder="(62) 99999-9999" data-testid="pf-whatsapp-input" />
          <p className="mt-1 text-base text-slate-600">O cliente só vê seu WhatsApp depois que você aceitar o pedido.</p></div>
        <ProviderServicesPick value={f.services} primary={f.primary_service} onChange={(services, primary_service) => setF({ ...f, services, primary_service })} />
        <div>
          <p className="po-label">Bairro onde você fica</p>
          <p className="mb-3 text-base text-slate-600">Usado só para calcular a distância aproximada. Nunca mostramos seu endereço.</p>
          <LocationPicker city={f.base_city} neighborhood={f.base_neighborhood} prefix="pf-base" onChange={(v) => setF({ ...f, base_city: v.city, base_neighborhood: v.neighborhood })} />
        </div>
        <AreasPick areas={f.areas} onChange={(areas) => setF({ ...f, areas })} />
        <div><label className="po-label" htmlFor="pf-desc">Descrição curta</label>
          <textarea id="pf-desc" maxLength={400} className="po-input min-h-[110px] py-3" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Ex.: 10 anos de experiência com instalações elétricas." data-testid="pf-description-input" /></div>
        <label className="flex items-start gap-3 text-base text-slate-700">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-6 w-6 accent-orange-500" data-testid="pf-terms-checkbox" />
          <span>Aceito os <Link to="/termos" className="font-semibold text-orange-600 underline">Termos de uso</Link> e a <Link to="/privacidade" className="font-semibold text-orange-600 underline">Política de privacidade</Link>.</span>
        </label>
        <button type="submit" disabled={busy} className="po-btn-primary min-h-[68px] w-full text-xl" data-testid="pf-submit-button">
          {busy && <Loader2 className="h-6 w-6 animate-spin" />} {p ? "Salvar alterações" : "Enviar cadastro"}
        </button>
      </form>
    </div>
  );
}
